import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { ShelterService } from '../shelter.service';
import {
  hasShelterCredential,
  isShelterCredential,
} from '../../constants/shelter-credentials.constant';
import { NGXLogger } from 'ngx-logger';

interface DecodedToken {
  'petszel-credentialtype'?: string | string[] | null;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  redirectRoute = '/shelter-dashboard';
  badCredentials = false;

  constructor(
    private readonly router: Router,
    private readonly shelterService: ShelterService,
    private logger: NGXLogger,
  ) {}

isLoggedIn(): boolean {
  const token = localStorage.getItem('token');

  if (!token) {
    this.logger.log('[AuthGuard] No token found');

    return false;
  }

  try {
    const decodedToken = jwtDecode<DecodedToken>(token);

    const credentialType =
      decodedToken['petszel-credentialtype'];

    const hasValidShelterCredential =
      hasShelterCredential(credentialType);

    this.logger.log('[AuthGuard] Token evaluated', {
      hasValidShelterCredential,
      credentialType,
    });

    return hasValidShelterCredential;
  } catch (error) {
    this.logger.log(
      '[AuthGuard] Token could not be decoded',
      error,
    );

    return false;
  }
}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    const normalizedUrl = state.url.toLowerCase();

    const isInvitationRoute =
      normalizedUrl.startsWith('/invitation/');

    this.logger.log('[AuthGuard] canActivate called', {
      routePath: route.routeConfig?.path,
      requestedUrl: state.url,
      isInvitationRoute,
    });

    /*
     * The invitation page is public.
     *
     * Allow it even when this guard is inherited from or applied by
     * a parent route.
     */
    if (isInvitationRoute) {
      this.logger.log(
        '[AuthGuard] Allowing public invitation route',
      );

      return true;
    }

    if (this.isLoggedIn()) {
      this.logger.log('[AuthGuard] Authenticated route allowed');

      return true;
    }

    this.logger.log(
      '[AuthGuard] Authentication failed; redirecting to login',
    );

    this.logout();

    return false;
  }

  logout(): void {
    this.logger.log('[AuthGuard] Logging out and navigating to login');

    localStorage.removeItem('token');
    this.shelterService.clearShelterName();

    void this.router.navigateByUrl('/');
  }

  redirectCallback(credentialType: string): void {
    if (!isShelterCredential(credentialType)) {
      return;
    }

    void this.router
      .navigateByUrl(this.redirectRoute)
      .then((success) => {
        if (!success) {
          this.logger.error(
            '[AuthGuard] Navigation to dashboard failed',
          );
        }
      });
  }

  logoutAndRedirect(): Observable<void> {
    this.logger.log('[AuthGuard] logoutAndRedirect called');

    this.shelterService.clearShelterName();
    this.logout();

    return of(undefined);
  }
}
